import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { articles } from '@/mocks/articles'
import { categories } from '@/mocks/categories'
import ArticleCard from '@/components/feature/ArticleCard'
import Breadcrumb from '@/components/feature/Breadcrumb'
import SEOHead from '@/components/feature/SEOHead'

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('common')

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [id])

  const article = articles.find((a) => a.id === Number(id))
  const category = categories.find((c) => c.slug === article?.category)
  const relatedArticles = articles
    .filter((a) => a.category === article?.category && a.id !== article?.id)
    .slice(0, 3)

  if (!article) {
    return (
      <main className="pt-20 md:pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-400 block mb-4"></i>
          <p className="text-foreground-600 text-lg mb-4">文章不存在或已删除</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
            {t('category.backToHome')}
          </Link>
        </div>
      </main>
    )
  }

  const formatCount = (n: number) => {
    if (n >= 10000) return `${(n / 10000).toFixed(1)}万`
    return n.toLocaleString()
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: { '@type': 'Person', name: article.author.name },
    datePublished: article.date,
    image: article.thumbnail,
    url: `https://kuaikuaimao.com/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '快猫短视频',
    },
  }

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('breadcrumb.home'), item: 'https://kuaikuaimao.com' },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || t('breadcrumb.category'),
        item: `https://kuaikuaimao.com/category/${article.category}`,
      },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  }

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.description}
        keywords={`快猫短视频,${article.tags.join(',')},${category?.name || ''}`}
        canonicalUrl={`https://kuaikuaimao.com/article/${article.id}`}
        ogType="article"
        articleData={{
          publishedTime: article.date,
          author: article.author.name,
          tags: article.tags,
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <main className="pt-20 md:pt-24">
        <article className="mx-auto px-4 md:px-6 max-w-4xl pb-12 md:pb-20">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), path: '/' },
              { label: category?.name || t('breadcrumb.category'), path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-6 md:mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 rounded-md bg-primary-100 text-primary-600 text-xs font-medium whitespace-nowrap hover:bg-primary-200 transition-colors"
              >
                {category?.name}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-md bg-background-100 text-foreground-600 text-xs whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-xl md:text-3xl text-foreground-950 leading-snug mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 flex-wrap text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <img src={article.author.avatar} alt={article.author.name} className="w-8 h-8 rounded-full object-cover" />
                <span className="font-medium text-foreground-800">{article.author.name}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatCount(article.views)}{t('article.views')}
              </span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-line"></i>
                {formatCount(article.likes)}{t('article.likes')}
              </span>
            </div>
          </header>

          <div className="rounded-2xl overflow-hidden mb-8 md:mb-10">
            <img
              src={article.thumbnail}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex items-center gap-4 mt-10 pt-8 border-t border-background-200 flex-wrap">
            <span className="text-sm text-foreground-500 whitespace-nowrap">{t('article.shareArticle')}:</span>
            <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-wechat-line"></i>
            </button>
            <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-weibo-line"></i>
            </button>
            <button className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-link"></i>
            </button>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-16">
            <div className="mx-auto px-4 md:px-6 max-w-7xl">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-8">
                {t('article.relatedArticles')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard
                    key={a.id}
                    id={a.id}
                    title={a.title}
                    description={a.description}
                    thumbnail={a.thumbnail}
                    category={a.category}
                    categoryName={categories.find((c) => c.slug === a.category)?.name || a.category}
                    author={a.author}
                    views={a.views}
                    likes={a.likes}
                    date={a.date}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  )
}